import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_CAPTIVE_PORTALS_LIST,
} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	responseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return a captive portal row identifier.
 * @param {object} item Canvas captive portal row
 * @return {*} identifier
 */
function captivePortalId(item) {
	// `captive_id` first, because that is the one Canvas actually sends.
	// `captivePortals.jsx` reads it for every row action and the list is
	// keyed on it; none of the three names tried below appear in the
	// response at all, so every row normalised to null and the lookup
	// reported an empty estate. Measured against a tenant holding 71
	// portals: "edit a captive portal" answered "There aren't any captive
	// portals yet."
	return item.captive_id ?? item.id ?? item.captive_portal_id ??
		item.portal_id;
}

/**
 * Normalize a captive portal row into an agent candidate.
 *
 * The name is what the table shows first and what a request names. The rest
 * travel as `field_values` keyed by the catalogue's own field ids, because
 * the candidate shape forbids extras - a profile type sent as a top-level
 * attribute is rejected outright.
 *
 * @param {object} item Canvas captive portal row
 * @return {object|null} agent candidate
 */
function normalizeCaptivePortal(item) {
	const id = captivePortalId(item);
	// `profile` is what the Name column renders - see `columnMapping` in
	// `captivePortals.jsx`. Same omission as the id above.
	const name = item.profile ?? item.name ?? item.portal_name ??
		item.captive_portal_name;
	if (id == null || !name) return null;
	const values = {portal_name: String(name)};
	// Which kind of portal this is. Canvas sends `iocp` and reads it the
	// same way the list does: 0 is an externally hosted portal, anything
	// else is one io Pass hosts.
	if (item.iocp != null) {
		values.profile_type = item.iocp === 0 ? 'External' : 'io Pass';
	} else if (item.profile_type) {
		values.profile_type = String(item.profile_type);
	}
	if (item.authentication_type) {
		values.authentication_type = String(item.authentication_type);
	}
	if (item.server_ip) values.server_ip = String(item.server_ip);
	if (item.splash_url) values.splash_url = String(item.splash_url);
	return {
		entity_type: 'captive_portal',
		entity_id: String(id),
		name: String(name),
		status: optionalText(item.status),
		site_id: null,
		site_name: null,
		serial_no: null,
		field_values: values,
	};
}

// Words that mean "no portal was actually named" - "edit the captive
// portal" with nothing left over. Unfiltered, a bare "portal" is a valid
// substring of every row's name and resolves to a target nobody chose.
const GENERIC_QUERIES = new Set([
	'captive portal', 'captive portals', 'portal', 'portals',
	'guest portal', 'guest portals', 'a portal', 'the portal',
]);

/**
 * Resolve captive portal candidates.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveCaptivePortals(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	if (GENERIC_QUERIES.has(query.toLowerCase())) {
		throw new Error('no_target_specified');
	}
	const loadRows = async (search, limit) => {
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: '';
		const url = `${ENDPOINT_URL}${GET_CAPTIVE_PORTALS_LIST}?limit=${limit}` +
			`&offset=0${searchParam}`;
		const {data} = await axios.get(url);
		return responseRows(decodeAgentResponse(data) || {});
	};
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('captive_portal', rows, captivePortalId);
		return {
			candidates: rows
				.map(normalizeCaptivePortal)
				.filter(Boolean)
				.slice(0, limit),
		};
	}
	const {rows, candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows,
		normalizeRow: normalizeCaptivePortal,
		fuzzy: params.fuzzy === true,
	});
	replaceResolvedEntities('captive_portal', rows, captivePortalId);
	return {candidates};
}

/**
 * Return a retained captive portal row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedCaptivePortal(entityId) {
	return getResolvedEntity('captive_portal', entityId);
}
